import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';
import { supportedLanguages } from '../i18n/config';

interface LanguageContextType {
  currentLanguage: string;
  supportedLanguages: typeof supportedLanguages;
  changeLanguage: (language: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useI18nextTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'en');

  const changeLanguage = async (language: string) => {
    if (!(language in supportedLanguages)) {
      setError(`Unsupported language: ${language}`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Change language in i18next
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);

      // Update local storage
      localStorage.setItem('preferredLanguage', language);

      // Update document direction and language
      const langInfo = supportedLanguages[language as keyof typeof supportedLanguages];
      document.documentElement.dir = langInfo.dir;
      document.documentElement.lang = language;

      // Save to backend if user is authenticated
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await fetch('/api/language/preferences', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ language }),
          });

          if (!response.ok) {
            console.warn('Failed to save language preference to backend:', response.statusText);
          }
        } catch (backendError) {
          console.warn('Failed to save language preference to backend:', backendError);
        }
      }
    } catch (err) {
      setError(`Failed to change language: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Language change error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load user's preferred language on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      const token = localStorage.getItem('accessToken');
      
      // First try to load from localStorage
      const storedLanguage = localStorage.getItem('preferredLanguage');
      if (storedLanguage && storedLanguage in supportedLanguages && storedLanguage !== currentLanguage) {
        await changeLanguage(storedLanguage);
      }

      // Then try to load from backend if authenticated
      if (token) {
        try {
          const response = await fetch('/api/language/preferences', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            const serverLanguage = data.data?.language;
            
            if (serverLanguage && serverLanguage !== currentLanguage) {
              await changeLanguage(serverLanguage);
            }
          }
        } catch (err) {
          console.warn('Failed to load language preference from backend:', err);
        }
      }
    };

    loadUserLanguage();
  }, []);

  // Listen for i18n language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const contextValue: LanguageContextType = {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    isLoading,
    error,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;